import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface CoreKeywordResult {
  topic: string;
  coreKeyword: string;
}

interface CoreKeywordMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  locationCode: number;
  languageName: string;
}

interface SerpResult {
  position: number;
  domain: string;
  url: string;
  title: string;
}

interface SerpResponse {
  keyword: string;
  results: SerpResult[];
}

interface SerpMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  keyword: string;
  locationCode: number;
  languageName: string;
}

interface UniversalEntity {
  keyword: string;
  normalizedKeyword: string;
  searchVolume: number;
  keywordDifficulty: number;
  coreKeyword: string | null;
  sources: string[];
  similarityScore: number;
}

interface UniversalEntityListMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  coreKeyword: string;
  locationCode: number;
  languageName: string;
}

interface UniversalEntityListStats {
  totalDiscovered: number;
  afterDeduplication: number;
  afterSimilarityFilter: number;
  fromRelatedDepth3: number;
  fromSuggestions: number;
}

interface UniversalEntityListCostBreakdown {
  coreKeyword: number;
  relatedDepth3: number;
  suggestions: number;
  total: number;
}

interface DomainCoverage {
  domain: string;
  entitiesCovered: number;
  totalKeywordsRanked: number;
  coveragePercentage: number;
  urls: Array<{
    url: string;
    title: string;
    position: number;
    entitiesCount: number;
  }>;
  missingEntities: string[];
}

interface DomainCoverageMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  domains: string[];
  locationCode: number;
  languageName: string;
}

interface DomainCoverageStats {
  totalEntities: number;
  totalDomains: number;
  totalApiCalls: number;
}

interface DomainCoverageCostBreakdown {
  rankedKeywords: number;
  total: number;
}

@Component({
  selector: 'app-topic-share-2',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './topic-share-2.html',
  styleUrl: './topic-share-2.css'
})
export class TopicShareComponent2 implements OnInit {
  topic: string = '';
  searchTopic: string = '';
  loading = false;
  loadingSerp = false;
  loadingUniversalList = false;
  loadingDomainCoverage = false;
  error: string | null = null;
  
  // Core keyword
  result: CoreKeywordResult | null = null;
  coreKeywordMetadata: CoreKeywordMetadata | null = null;
  coreKeywordCost: number = 0;
  
  // SERP results
  serpResults: SerpResponse | null = null;
  serpMetadata: SerpMetadata | null = null;
  serpCost: number = 0;
  
  // Universal entity list
  universalEntities: UniversalEntity[] = [];
  universalEntityListStats: UniversalEntityListStats | null = null;
  universalEntityListCost: UniversalEntityListCostBreakdown | null = null;
  universalEntityListMetadata: UniversalEntityListMetadata | null = null;
  showAllEntities: boolean = false;
  
  // Domain coverage
  domainCoverage: DomainCoverage[] = [];
  domainCoverageStats: DomainCoverageStats | null = null;
  domainCoverageCost: DomainCoverageCostBreakdown | null = null;
  domainCoverageMetadata: DomainCoverageMetadata | null = null;
  
  // Cost tracking
  totalCost: number = 0;
  
  // Defaults
  defaultLocationCode: number = 2840;
  defaultLanguageName: string = 'English';

  constructor(private http: HttpClient) {}

  ngOnInit() {
    // Component initialization
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  }

  async discoverCoreKeyword() {
    if (!this.topic.trim()) {
      this.error = 'Please enter a topic';
      return;
    }

    this.loading = true;
    this.error = null;
    this.searchTopic = this.topic.trim();
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/core-keyword`,
        {
          topic: this.searchTopic
        }
      ).toPromise();

      if (response.success) {
        this.result = response.data;
        this.coreKeywordMetadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          topic: this.searchTopic,
          locationCode: this.defaultLocationCode,
          languageName: this.defaultLanguageName
        };

        // Track cost from core keyword API (only if fetched from API)
        if (this.coreKeywordMetadata && this.coreKeywordMetadata.dataSource === 'api' && response.data?.tasks?.[0]?.cost) {
          this.coreKeywordCost = response.data.tasks[0].cost;
          this.totalCost += this.coreKeywordCost;
        }

        console.log(`📊 Core keyword result (source: ${this.coreKeywordMetadata?.dataSource || 'unknown'}, cost: $${this.coreKeywordCost.toFixed(4)})`);

        // Automatically discover SERP results
        await this.discoverSerpResults();
      } else {
        this.error = response.message || 'Failed to discover core keyword';
      }
    } catch (error: any) {
      console.error('Error discovering core keyword:', error);
      this.error = error.error?.message || error.message || 'Failed to discover core keyword';
    } finally {
      this.loading = false;
    }
  }

  async discoverSerpResults() {
    if (!this.result || !this.result.coreKeyword) {
      return;
    }

    this.loadingSerp = true;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/serp`,
        {
          keyword: this.searchTopic
        }
      ).toPromise();

      if (response.success) {
        this.serpResults = response.data;
        this.serpMetadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          keyword: this.searchTopic,
          locationCode: this.defaultLocationCode,
          languageName: this.defaultLanguageName
        };

        // Track cost from SERP API (only if fetched from API)
        if (this.serpMetadata && this.serpMetadata.dataSource === 'api' && response.data?.tasks?.[0]?.cost) {
          this.serpCost = response.data.tasks[0].cost;
          this.totalCost += this.serpCost;
        }

        console.log(`📊 SERP results (source: ${this.serpMetadata?.dataSource || 'unknown'}, cost: $${this.serpCost.toFixed(4)})`);
      } else {
        this.error = response.message || 'Failed to discover SERP results';
      }
    } catch (error: any) {
      console.error('Error discovering SERP results:', error);
      this.error = error.error?.message || error.message || 'Failed to discover SERP results';
    } finally {
      this.loadingSerp = false;
    }
  }

  getUniqueDomains(): string[] {
    if (!this.serpResults?.results) {
      return [];
    }
    const domainSet = new Set<string>();
    this.serpResults.results.forEach(result => {
      const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
      domainSet.add(domain);
    });
    return Array.from(domainSet);
  }

  async buildUniversalEntityList() {
    if (!this.searchTopic || !this.result?.coreKeyword) {
      this.error = 'Please discover core keyword first';
      return;
    }

    this.loadingUniversalList = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share-2/build-universal-entities`,
        {
          topic: this.searchTopic
        }
      ).toPromise();

      if (response.success) {
        this.universalEntities = response.data.entities || [];
        this.universalEntityListStats = response.data.stats || null;
        this.universalEntityListCost = response.data.costBreakdown || null;
        this.universalEntityListMetadata = response.metadata || null;

        // Add universal entity list cost to total
        if (this.universalEntityListCost) {
          this.totalCost += this.universalEntityListCost.total;
        }

        console.log(`✅ Built universal entity list: ${this.universalEntities.length} entities (cost: $${this.universalEntityListCost?.total.toFixed(4) || '0.0000'})`);
      } else {
        this.error = response.message || 'Failed to build universal entity list';
      }
    } catch (error: any) {
      console.error('Error building universal entity list:', error);
      this.error = error.error?.message || error.message || 'Failed to build universal entity list';
    } finally {
      this.loadingUniversalList = false;
    }
  }

  async checkDomainCoverage() {
    if (!this.serpResults || !this.universalEntities.length) {
      this.error = 'Please discover SERP results and build universal entity list first';
      return;
    }

    const domains = this.getUniqueDomains();
    if (domains.length === 0) {
      this.error = 'No domains found in SERP results';
      return;
    }

    const entityList = this.universalEntities.map(e => e.normalizedKeyword);

    this.loadingDomainCoverage = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share-2/check-domain-coverage`,
        {
          topic: this.searchTopic,
          domains: domains,
          entityList: entityList
        }
      ).toPromise();

      if (response.success) {
        this.domainCoverage = response.data.domains || [];
        this.domainCoverageStats = response.data.stats || null;
        this.domainCoverageCost = response.data.costBreakdown || null;
        this.domainCoverageMetadata = response.metadata || null;

        // Add domain coverage cost to total
        if (this.domainCoverageCost) {
          this.totalCost += this.domainCoverageCost.total;
        }

        console.log(`✅ Calculated coverage for ${this.domainCoverage.length} domains (cost: $${this.domainCoverageCost?.total.toFixed(4) || '0.0000'})`);
      } else {
        this.error = response.message || 'Failed to check domain coverage';
      }
    } catch (error: any) {
      console.error('Error checking domain coverage:', error);
      this.error = error.error?.message || error.message || 'Failed to check domain coverage';
    } finally {
      this.loadingDomainCoverage = false;
    }
  }

  getDisplayedEntities(): UniversalEntity[] {
    if (this.showAllEntities) {
      return this.universalEntities;
    }
    return this.universalEntities.slice(0, 50);
  }

  getSimilarityColorClass(score: number): string {
    if (score >= 0.8) return 'similarity-high';
    if (score >= 0.6) return 'similarity-medium';
    if (score >= 0.5) return 'similarity-low';
    return 'similarity-very-low';
  }

  onNewSearch() {
    // Clear all data on new search
    this.result = null;
    this.coreKeywordMetadata = null;
    this.coreKeywordCost = 0;
    this.serpResults = null;
    this.serpMetadata = null;
    this.serpCost = 0;
    this.universalEntities = [];
    this.universalEntityListStats = null;
    this.universalEntityListCost = null;
    this.universalEntityListMetadata = null;
    this.domainCoverage = [];
    this.domainCoverageStats = null;
    this.domainCoverageCost = null;
    this.domainCoverageMetadata = null;
    this.totalCost = 0;
    this.showAllEntities = false;
  }
}

